import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Un pedido PARA LLEVAR enviado desde este dispositivo, aún en curso. */
export interface ActiveOrder {
  orderId: string
  /** Sesión a la que pertenece — si la sesión cambia, no aplica. */
  tableSessionId: string
  /** Código de recolección si el backend ya lo devolvió al crear (para listarlos). */
  pickupCode?: string | null
  /** ms epoch de cuándo se envió — para ordenarlos cronológicamente. */
  placedAt: number
}

interface ActiveOrderState {
  orders: ActiveOrder[]
  add: (o: ActiveOrder) => void
  /** Quita SOLO ese pedido (el tracker suelta el suyo al entregarse/cancelarse). */
  removeByOrderId: (orderId: string) => void
  clear: () => void
}

/**
 * Rastro persistido de los pedidos takeout en curso DE ESTE DISPOSITIVO.
 *
 * Por qué es una LISTA: para llevar, cada "Enviar a cocina" crea un pedido
 * INDEPENDIENTE con su propio código de recolección (no se puede "agregar más"
 * a uno ya enviado). Si el comensal hace 2+ pedidos (olvidó algo), debe poder
 * ver TODOS juntos con sus códigos — antes se guardaba sólo el último y el
 * anterior "desaparecía" (reporte MilCaguamas 2026-07-20).
 *
 * El Menú pinta un banner con la lista; cada tracker enlaza a los hermanos.
 * Se limpia: al entregarse/cancelarse cada pedido (OrderTracker → removeByOrderId),
 * o "Cerrar y salir" (NintaiGOHeader → clear).
 */
export const useActiveOrderStore = create<ActiveOrderState>()(
  persist(
    (set, get) => ({
      orders: [],
      add: (o) => {
        // Dedupe por orderId (un reintento no duplica la tarjeta).
        const rest = get().orders.filter((x) => x.orderId !== o.orderId)
        set({ orders: [...rest, o] })
      },
      removeByOrderId: (orderId) =>
        set({ orders: get().orders.filter((x) => x.orderId !== orderId) }),
      clear: () => set({ orders: [] }),
    }),
    {
      name: 'dhk8-guest-active-order',
      version: 2,
      // v1 guardaba un único `active` → migrar a lista sin perder el pedido vivo.
      migrate: (persisted: unknown) => {
        const p = persisted as { active?: Omit<ActiveOrder, 'placedAt'> | null; orders?: ActiveOrder[] } | undefined
        if (p?.orders) return { orders: p.orders }
        if (p?.active) return { orders: [{ ...p.active, placedAt: Date.now() }] }
        return { orders: [] }
      },
    },
  ),
)
